"use client";

import type {
  DispatchBoard,
  DispatchJob,
  DispatchNurseRoute,
} from "@vellum/vellum-api";
import { useEffect, useMemo, useState } from "react";

import { Alert } from "../components/Alert";
import { Card } from "../components/Card";
import { Loading } from "../components/Loading";
import { SelectV2, type SelectOption } from "../components/SelectV2";
import { useQuery } from "../hooks/useQuery";
import { POLL_INTERVAL_MS, indexBoard } from "./board";
import { DispatchMap } from "./DispatchMap";
import { DispatchQueue } from "./DispatchQueue";

export type DispatchBoardViewProps = {
  /**
   * Fetches the board for these regions (every region when empty). The host
   * app supplies its own API client, so the view works in any app.
   */
  load: (
    regionIds: string[],
  ) => Promise<{ data?: DispatchBoard; error?: unknown }>;
  /**
   * Fetches one nurse's legs along the actual roads, for when they're
   * highlighted. Kept off the board itself so the poll stays light.
   */
  loadNurseRoute: (
    nurseId: string,
  ) => Promise<{ data?: DispatchNurseRoute; error?: unknown }>;
  /** Where a job's details page is, linked from its row in the side panel. */
  jobHref: (job: DispatchJob) => string;
  regionIds: string[];
  onRegionIdsChange: (regionIds: string[]) => void;
};

function LegendItem({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className={swatch} />
      {label}
    </span>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 text-xs text-gray-600">
      <LegendItem
        swatch="h-3 w-3 border-2 border-stone-900 bg-stone-900"
        label="Jobs, all assigned"
      />
      <LegendItem
        swatch="h-3 w-3 border-2 border-stone-900 bg-white"
        label="Has an unassigned job"
      />
      <LegendItem
        swatch="h-3 w-3 border-2 border-stone-900 bg-amber-700"
        label="Has a late job (!)"
      />
      <LegendItem
        swatch="h-3 w-3 border-2 border-stone-900 bg-stone-200"
        label="All visited"
      />
      <LegendItem swatch="h-1 w-5 rounded bg-stone-900" label="Next drive" />
      <LegendItem
        swatch="h-0 w-5 border-t-2 border-dashed border-stone-900"
        label="Later drives"
      />
      <LegendItem swatch="h-0.5 w-5 bg-stone-400" label="Driven" />
      <LegendItem
        swatch="h-3 w-3 rounded-full border-2 border-stone-900 bg-stone-900"
        label="On site"
      />
      <LegendItem
        swatch="h-3 w-3 rounded-full border-2 border-dashed border-stone-900 bg-white"
        label="Between stops"
      />
      <LegendItem
        swatch="h-3 w-3 rounded-full border-2 border-stone-400 bg-white"
        label="Not started (home)"
      />
      <LegendItem
        swatch="h-2 w-2 rounded-full bg-stone-400"
        label="No jobs today"
      />
    </div>
  );
}

/**
 * Today's dispatch board: a map of facilities and nurse routes beside the
 * unassigned jobs and everyone's stops by ETA. Refreshes every few seconds
 * while the page is visible. Positions are inferred from check-ins.
 */
export function DispatchBoardView({
  load,
  loadNurseRoute,
  jobHref,
  regionIds,
  onRegionIdsChange,
}: DispatchBoardViewProps) {
  const regionKey = regionIds.join(",");
  // Each board carries the regions it was loaded for, so the map re-frames
  // when the new regions' board arrives rather than on the click.
  const query = useQuery<{ regionKey: string; board: DispatchBoard }>(
    async () => {
      const result = await load(regionIds);
      return result.data
        ? { data: { regionKey, board: result.data } }
        : { error: result.error };
    },
    "Something went wrong loading the dispatch board.",
    [regionKey],
  );
  const { refetch } = query;
  const board = query.data?.board ?? null;

  useEffect(() => {
    const timer = window.setInterval(() => {
      // A hidden tab doesn't need a fresh board; the next visible tick catches up.
      if (document.visibilityState === "visible") void refetch();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [refetch]);

  const [selectedNurseId, setSelectedNurseId] = useState<string | null>(null);
  const index = useMemo(() => indexBoard(board), [board]);
  // A nurse who drops off the board (unassigned, or another region) is unselected.
  const selected =
    selectedNurseId && index.nurses.has(selectedNurseId)
      ? selectedNurseId
      : null;

  // The highlighted nurse's roads are refetched when their legs change or a
  // road gets cached, not on every poll, and not at all while none is cached.
  const selectedNurse = selected ? index.nurses.get(selected) : undefined;
  const roadKey = selectedNurse
    ? `${selectedNurse.id}|${selectedNurse.legs
        .map((leg) => `${leg.job_id}:${leg.has_road ? 1 : 0}`)
        .join(",")}`
    : "";
  const roads = useQuery<DispatchNurseRoute>(
    () => loadNurseRoute(selected!),
    "Couldn't load this nurse's roads; showing straight lines.",
    [roadKey],
    { enabled: Boolean(selectedNurse?.legs.some((leg) => leg.has_road)) },
  );

  const regionOptions: SelectOption<string>[] = useMemo(
    () =>
      (board?.regions ?? []).map((region) => ({
        value: region.id,
        label: region.name,
      })),
    [board?.regions],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full sm:max-w-xl">
          <SelectV2<string, true>
            isMulti
            label="Regions"
            name="dispatch-regions"
            placeholder="All regions"
            options={regionOptions}
            value={regionOptions.filter((option) =>
              regionIds.includes(option.value),
            )}
            onChange={(options) =>
              onRegionIdsChange(options.map((option) => option.value))
            }
          />
        </div>
        {board && (
          <p className="text-xs text-gray-500" aria-live="polite">
            Updated{" "}
            {new Date(board.generated_at).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              second: "2-digit",
            })}
            {" · "}refreshes every {POLL_INTERVAL_MS / 1000}s
          </p>
        )}
      </div>

      {query.error && <Alert>{query.error}</Alert>}
      {selected && roads.error && <Alert>{roads.error}</Alert>}

      <div className="flex flex-col gap-3 xl:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Card className="relative h-[480px] overflow-hidden xl:h-[calc(100vh-14rem)] xl:min-h-[560px]">
            <DispatchMap
              board={board}
              index={index}
              selectedNurseId={selected}
              onSelectNurse={setSelectedNurseId}
              selectedRoute={roads.data}
              frameKey={query.data?.regionKey ?? null}
            />
            {query.loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                <Loading />
              </div>
            )}
          </Card>
          <Legend />
        </div>
        <aside
          aria-label="Jobs"
          className="xl:h-[calc(100vh-14rem)] xl:min-h-[560px] xl:w-[420px] xl:shrink-0 xl:overflow-y-auto"
        >
          {board && (
            <DispatchQueue
              board={board}
              index={index}
              selectedNurseId={selected}
              onSelectNurse={setSelectedNurseId}
              jobHref={jobHref}
            />
          )}
        </aside>
      </div>
    </div>
  );
}
